// =============================================================================
// features/sales/agenda/AgendaStates.tsx — loading (skeleton), vazio (sem
// agenda / por filtro) e erro recuperável (doc 26 §6.1 "Estados
// obrigatórios"). Nunca spinner sozinho (norma do DS).
// =============================================================================

import * as React from 'react';

import { Button } from '../../../components/ui/Button';

// ---------------------------------------------------------------------------
// Loading — skeleton de cards (espelha o shape do AppointmentCard)
// ---------------------------------------------------------------------------

export function AppointmentListSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Carregando agenda">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="rounded-md border border-border bg-surface-1 p-4 flex items-center gap-4 animate-pulse"
          style={{ boxShadow: 'var(--elev-2)' }}
          aria-hidden="true"
        >
          <div
            className="h-9 w-9 rounded-pill shrink-0"
            style={{ background: 'var(--surface-muted)' }}
          />
          <div className="flex-1 flex flex-col gap-2">
            <div className="h-3.5 w-40 rounded" style={{ background: 'var(--surface-muted)' }} />
            <div className="h-3 w-64 rounded" style={{ background: 'var(--surface-muted)' }} />
          </div>
          <div
            className="h-8 w-28 rounded-sm shrink-0"
            style={{ background: 'var(--surface-muted)' }}
          />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vazio — sem agenda (nenhum filtro extra) vs. por filtro
// ---------------------------------------------------------------------------

export function AgendaEmptyState({
  filtered,
  hasOtherPages = false,
  onClearFilters,
}: {
  filtered: boolean;
  /** true quando a página atual está vazia por filtro, mas o servidor tem mais páginas — ver AppointmentList. */
  hasOtherPages?: boolean;
  onClearFilters: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center px-4">
      <svg
        viewBox="0 0 48 48"
        fill="none"
        stroke="currentColor"
        strokeWidth={1}
        className="w-12 h-12 text-ink-4"
        aria-hidden="true"
      >
        <rect x="8" y="10" width="32" height="30" rx="3" />
        <path d="M8 18h32M16 6v8M32 6v8" strokeLinecap="round" />
        {!filtered && <path d="M18 28l4 4 8-8" strokeLinecap="round" strokeLinejoin="round" />}
      </svg>
      <div className="flex flex-col gap-1">
        <p
          className="font-display font-semibold text-ink"
          style={{ fontSize: 'var(--text-base)', letterSpacing: '-0.02em' }}
        >
          {filtered ? 'Nada encontrado com esses filtros' : 'Nenhum compromisso por aqui'}
        </p>
        <p className="font-sans text-sm text-ink-3 max-w-xs">
          {filtered
            ? hasOtherPages
              ? 'Nenhum compromisso desta página bate com os filtros. Use a paginação abaixo para ver outras páginas, ou ajuste os filtros.'
              : 'Ajuste ou limpe os filtros para ver outros compromissos desta aba.'
            : 'Quando você agendar um retorno, ele aparece aqui — organizado por vencimento e prioridade.'}
        </p>
      </div>
      {filtered && (
        <Button variant="outline" size="sm" onClick={onClearFilters}>
          Limpar filtros
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Erro recuperável
// ---------------------------------------------------------------------------

export function AgendaErrorState({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-4">
      <svg
        viewBox="0 0 48 48"
        fill="none"
        stroke="currentColor"
        strokeWidth={1}
        className="w-12 h-12 text-danger"
        aria-hidden="true"
      >
        <circle cx="24" cy="24" r="18" />
        <path d="M24 16v10M24 30v2" strokeLinecap="round" />
      </svg>
      <div className="flex flex-col gap-1">
        <p
          className="font-display font-semibold text-ink"
          style={{ fontSize: 'var(--text-base)', letterSpacing: '-0.02em' }}
        >
          Não foi possível carregar a agenda
        </p>
        <p className="font-sans text-sm text-ink-3 max-w-xs">
          Verifique sua conexão e tente novamente.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Tentar novamente
      </Button>
    </div>
  );
}
